import { DND_DRAG_HANDLE_ATTR, DND_DROPPABLE_ATTR } from './attributes'
import { autoScroll, collectScrollParents } from './autoScroll'
import { changeDropTargetEdge, clearDropTarget, currentDropTarget, enterDropTarget } from './dropTarget'
import { computeInsertionEdge, exceedsDragThreshold } from './geometry'
import { createGhost, positionGhost, watchSourceClass } from './ghost'
import { emitDrop, isDropValid } from './registry'
import { type DndScope, type Gesture, inScope, nearestWithAttribute, readDropId } from './scope'
import { type ActiveDrag, endGesture, type PendingDrag, runningGesture, setBodyDragging, setRunningGesture } from './session'
import type { DndEdge } from './types'

// Pointer path: a press becomes a pending drag, and turns into an active drag
// with a ghost once it travels past the threshold.

function pressStartedInHandle(event: PointerEvent, sourceElement: HTMLElement): boolean {
    if (sourceElement.querySelector(`[${DND_DRAG_HANDLE_ATTR}]`) === null) return true
    const target = event.target
    if (!(target instanceof Element)) return false
    const handleAncestor = target.closest(`[${DND_DRAG_HANDLE_ATTR}]`)
    return handleAncestor !== null && sourceElement.contains(handleAncestor)
}

/** Whether a press on the draggable may start a drag. */
export function isDragPress(event: PointerEvent, sourceElement: HTMLElement): boolean {
    // Non-primary buttons (e.g. right-click) are gestures the app owns, not drags.
    if (event.button !== 0) return false
    // When the draggable declares a handle, presses elsewhere belong to the content inside.
    return pressStartedInHandle(event, sourceElement)
}

export function pressDraggable(gesture: Gesture, event: PointerEvent): void {
    setRunningGesture({ ...gesture, kind: 'pending', pointerId: event.pointerId, startX: event.clientX, startY: event.clientY })
}

/** The running pointer gesture, if `event` comes from the pointer that started it. */
function gestureOfPointer(event: PointerEvent): PendingDrag | ActiveDrag | null {
    const running = runningGesture()
    if (running === null || running.kind === 'keyboard' || running.pointerId !== event.pointerId) return null
    return running
}

function moveGhost(drag: ActiveDrag): void {
    positionGhost(drag.ghost, drag.pointerX - drag.offsetX, drag.pointerY - drag.offsetY)
}

function hitTestDroppable(x: number, y: number, scope: DndScope): HTMLElement | null {
    const underPointer = document.elementFromPoint(x, y)
    if (underPointer === null) return null
    // Innermost droppable of this kit; droppables of other kits are skipped.
    const droppable = nearestWithAttribute(underPointer, DND_DROPPABLE_ATTR, scope.kitId)
    if (!(droppable instanceof HTMLElement)) return null
    // A droppable in another zone of the same kit counts as a miss.
    return inScope(droppable, scope) ? droppable : null
}

// "Insert before/after this element" only makes sense for a target that takes
// the drop and is not the dragged element itself (an element that is both
// draggable and droppable). Measured only here, inside the frame handler.
function insertionEdgeFor(drag: ActiveDrag, element: HTMLElement, valid: boolean, pointerY: number): DndEdge | null {
    if (!valid || element === drag.sourceElement) return null
    return computeInsertionEdge(pointerY, element.getBoundingClientRect())
}

function updateDropTarget(drag: ActiveDrag): void {
    const y = drag.pointerY
    const element = hitTestDroppable(drag.pointerX, y, drag.scope)
    if (element === null) {
        clearDropTarget(drag)
        return
    }
    const current = currentDropTarget()
    if (current !== null && current.element === element) {
        // Same target: reuse the cached canDrop verdict, but re-emit when the
        // pointer crosses the midpoint so consumers can move the gap indicator.
        const edge = insertionEdgeFor(drag, element, current.valid, y)
        if (current.edge !== edge) changeDropTargetEdge(drag, edge)
        return
    }
    // Silent clear: the single transition below reports the new target, so
    // the intermediate leave must not emit on its own.
    clearDropTarget(drag, false)
    const dropId = readDropId(element)
    // Rejected hits are still marked so CSS can show why.
    const valid = isDropValid(drag, element, dropId)
    enterDropTarget(drag, { element, dropId, valid, edge: insertionEdgeFor(drag, element, valid, y) })
}

function processFrame(drag: ActiveDrag): void {
    moveGhost(drag)
    updateDropTarget(drag)
    autoScroll(drag.scrollParents, drag.pointerX, drag.pointerY)
}

// Runs every frame while a drag is active, not only after pointer moves: a
// hover handler may re-render the page and move droppables under a still
// pointer, so the hovered element must be re-checked after React commits.
// Same for auto-scroll while the pointer rests at an edge. An unchanged hover costs one elementFromPoint and two ancestor walks.
function scheduleFrame(drag: ActiveDrag): void {
    if (drag.frameId !== null || runningGesture() !== drag) return
    drag.frameId = window.requestAnimationFrame(() => {
        drag.frameId = null
        if (runningGesture() !== drag) return
        processFrame(drag)
        scheduleFrame(drag)
    })
}

function startDrag(pending: PendingDrag, clientX: number, clientY: number): void {
    const rect = pending.sourceElement.getBoundingClientRect()
    const ghost = createGhost(pending.sourceElement, rect)
    if (ghost === null) {
        endGesture()
        return
    }
    // Watch before publishing the drag: useIsDragging may re-render synchronously and change the source class in that turn.
    const stopClassSync = watchSourceClass(pending.sourceElement, ghost)

    const body = document.body
    const savedBodyUserSelect = body.style.userSelect
    body.style.userSelect = 'none'
    setBodyDragging(true)
    body.appendChild(ghost)

    const drag: ActiveDrag = {
        kind: 'active',
        pointerId: pending.pointerId,
        scope: pending.scope,
        payload: pending.payload,
        sourceElement: pending.sourceElement,
        ghost,
        offsetX: clientX - rect.left,
        offsetY: clientY - rect.top,
        pointerX: clientX,
        pointerY: clientY,
        scrollParents: collectScrollParents(pending.sourceElement),
        frameId: null,
        stopClassSync,
        savedBodyUserSelect,
    }
    setRunningGesture(drag)
    moveGhost(drag)
    scheduleFrame(drag)
}

export function onPointerMove(event: PointerEvent): void {
    const running = gestureOfPointer(event)
    if (running?.kind === 'active') {
        running.pointerX = event.clientX
        running.pointerY = event.clientY
        scheduleFrame(running)
        return
    }
    if (running?.kind !== 'pending') return
    if (!exceedsDragThreshold(running.startX, running.startY, event.clientX, event.clientY)) return
    startDrag(running, event.clientX, event.clientY)
}

// The browser still fires click on the nearest common ancestor of the press and
// release targets (the ghost ignores pointer events), so a drop can open a
// popover or re-trigger the chip's own onClick. That click is dispatched in the
// same task as pointerup; the guard expires after it so a release that produces
// no click (e.g. outside the window) cannot swallow the next real one.
function suppressClickAfterDrag(): void {
    const swallow = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation()
    }
    window.addEventListener('click', swallow, { capture: true, once: true })
    window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)
}

export function onPointerUp(event: PointerEvent): void {
    const drag = gestureOfPointer(event)
    if (drag?.kind === 'pending') {
        // Released before crossing the threshold: an ordinary click, not a drag.
        endGesture()
        return
    }
    if (drag?.kind !== 'active') return
    suppressClickAfterDrag()
    // A throwing canDrop or onDrop must still end the gesture: otherwise the
    // ghost and listeners stay, and the next pointerup anywhere drops again.
    try {
        // The last rAF frame may lag behind the release point, so re-resolve the
        // target synchronously before deciding whether this pointerup is a drop.
        drag.pointerX = event.clientX
        drag.pointerY = event.clientY
        updateDropTarget(drag)
        const target = currentDropTarget()
        // Re-check at drop time: app state may have changed since the target was first hit.
        if (target !== null && isDropValid(drag, target.element, target.dropId)) {
            // Recompute at the release point so onDrop matches the final half,
            // even if the last rAF frame lagged behind the pointer.
            emitDrop(drag, target.element, target.dropId, insertionEdgeFor(drag, target.element, true, event.clientY))
        }
    } finally {
        endGesture()
    }
}

export function onPointerCancel(event: PointerEvent): void {
    // The browser took the gesture back (e.g. incoming call on touch): never drop.
    if (gestureOfPointer(event) !== null) endGesture()
}
